/**
 * Language Service
 * Handles language persistence, API fetching, and language management
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import i18n from '../i18n/i18n.config';
import { mapApiLangToI18n, mapI18nLangToApi, I18nLanguageCode, ApiLanguageCode } from '../i18n/languageMapper';

// Storage key for persisting language preference
const LANGUAGE_STORAGE_KEY = 'app_language';

/**
 * Storage wrapper for cross-platform compatibility
 */
const storage = {
  async getItem(key: string): Promise<string | null> {
    try {
      if (Platform.OS === 'web') {
        return localStorage.getItem(key);
      }
      return await AsyncStorage.getItem(key);
    } catch (error) {
      console.error('[Language Service] Error reading from storage:', error);
      return null;
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        localStorage.setItem(key, value);
      } else {
        await AsyncStorage.setItem(key, value);
      }
    } catch (error) {
      console.error('[Language Service] Error writing to storage:', error);
    }
  },

  async removeItem(key: string): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        localStorage.removeItem(key);
      } else {
        await AsyncStorage.removeItem(key);
      }
    } catch (error) {
      console.error('[Language Service] Error removing from storage:', error);
    }
  },
};

/**
 * Language Service class
 */
class LanguageService {
  private initialized = false;

  /**
   * Fetches language preference from API
   * @param apiUrl - API endpoint URL
   * @param authToken - Optional authentication token
   * @returns API language code or null if failed
   */
  async fetchLanguageFromAPI(apiUrl: string, authToken?: string): Promise<ApiLanguageCode | null> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.warn('[Language Service] API request failed:', response.status);
        return null;
      }

      const data = await response.json();

      // Adjust this based on your API response structure
      // Assuming API returns { language: 'en' } or { data: { language: 'en' } }
      const apiLangCode = data.language || data.data?.language || null;

      if (apiLangCode) {
        console.log('[Language Service] Fetched language from API:', apiLangCode);
        return apiLangCode;
      }

      return null;
    } catch (error) {
      console.error('[Language Service] Error fetching language from API:', error);
      return null;
    }
  }

  /**
   * Updates language preference on API
   * @param apiUrl - API endpoint URL
   * @param language - Language code to save
   * @param authToken - Optional authentication token
   * @returns True if successful, false otherwise
   */
  async updateLanguageOnAPI(
    apiUrl: string,
    language: ApiLanguageCode,
    authToken?: string
  ): Promise<boolean> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ language }),
      });

      if (!response.ok) {
        console.warn('[Language Service] Failed to update language on API:', response.status);
        return false;
      }

      console.log('[Language Service] Successfully updated language on API:', language);
      return true;
    } catch (error) {
      console.error('[Language Service] Error updating language on API:', error);
      return false;
    }
  }

  /**
   * Gets stored language from local storage
   * @returns Stored i18n language code or null
   */
  async getStoredLanguage(): Promise<I18nLanguageCode | null> {
    try {
      const storedLang = await storage.getItem(LANGUAGE_STORAGE_KEY);

      if (storedLang) {
        console.log('[Language Service] Retrieved stored language:', storedLang);
        return storedLang as I18nLanguageCode;
      }

      return null;
    } catch (error) {
      console.error('[Language Service] Error getting stored language:', error);
      return null;
    }
  }

  /**
   * Saves language to local storage
   * @param langCode - i18n language code to store
   */
  async saveLanguage(langCode: I18nLanguageCode): Promise<void> {
    try {
      await storage.setItem(LANGUAGE_STORAGE_KEY, langCode);
      console.log('[Language Service] Saved language to storage:', langCode);
    } catch (error) {
      console.error('[Language Service] Error saving language:', error);
    }
  }

  /**
   * Clears stored language preference
   */
  async clearStoredLanguage(): Promise<void> {
    try {
      await storage.removeItem(LANGUAGE_STORAGE_KEY);
      console.log('[Language Service] Cleared stored language');
    } catch (error) {
      console.error('[Language Service] Error clearing language:', error);
    }
  }

  /**
   * Changes the current app language
   * @param langCode - i18n language code
   * @param persist - Whether to persist the change
   */
  async changeLanguage(langCode: I18nLanguageCode, persist = true): Promise<void> {
    try {
      console.log('[Language Service] 🔄 Changing language to:', langCode, '| Persist:', persist);
      console.log('[Language Service] 📍 Current i18n language:', i18n.language);

      await i18n.changeLanguage(langCode);

      console.log('[Language Service] ✅ i18n.changeLanguage() completed');
      console.log('[Language Service] 📍 New i18n language:', i18n.language);

      if (persist) {
        await this.saveLanguage(langCode);
        console.log('[Language Service] 💾 Language persisted to storage');
      }

      console.log('[Language Service] ✅ Changed language to:', langCode);
    } catch (error) {
      console.error('[Language Service] ❌ Error changing language:', error);
      throw error;
    }
  }

  /**
   * Gets current active language
   * @returns Current i18n language code
   */
  getCurrentLanguage(): I18nLanguageCode {
    return i18n.language as I18nLanguageCode;
  }

  /**
   * Initializes language on app startup
   * Priority: Stored Language > API Language > Default (en)
   * @param apiUrl - Optional API endpoint to fetch language preference
   * @param authToken - Optional authentication token
   */
  async initializeLanguage(apiUrl?: string, authToken?: string): Promise<I18nLanguageCode> {
    console.log('[Language Service] 🔄 Starting language initialization...');

    if (this.initialized) {
      const currentLang = this.getCurrentLanguage();
      console.log('[Language Service] ✅ Already initialized with:', currentLang);
      return currentLang;
    }

    try {
      // Step 1: Check for stored language preference (for instant load)
      const storedLang = await this.getStoredLanguage();
      console.log('[Language Service] 📦 Stored language:', storedLang || 'none');

      if (storedLang) {
        console.log('[Language Service] ⚡ Fast-loading with stored language:', storedLang);
        await this.changeLanguage(storedLang, false);
        // Don't mark as initialized yet - still need to check API
      }

      // Step 2: Try to fetch from API if URL provided (may override stored)
      if (apiUrl) {
        console.log('[Language Service] 🌐 Fetching language from API...');
        const apiLangCode = await this.fetchLanguageFromAPI(apiUrl, authToken);
        console.log('[Language Service] 📡 API returned language:', apiLangCode || 'none');

        if (apiLangCode) {
          const i18nLangCode = mapApiLangToI18n(apiLangCode);
          console.log('[Language Service] 🔄 Mapping API code:', apiLangCode, '→', i18nLangCode);

          // Only update if different from stored
          if (i18nLangCode !== storedLang) {
            console.log('[Language Service] 🔃 API language differs from stored, updating...');
            await this.changeLanguage(i18nLangCode, true);
          } else {
            console.log('[Language Service] ✅ API language matches stored, no update needed');
          }

          this.initialized = true;
          console.log('[Language Service] ✅ Initialization complete with:', i18nLangCode);
          return i18nLangCode;
        }
      }

      // Step 3: Use stored or default language
      if (storedLang) {
        this.initialized = true;
        console.log('[Language Service] ✅ Initialization complete with stored:', storedLang);
        return storedLang;
      }

      // Step 4: Use default language (English)
      console.log('[Language Service] 🔤 No stored/API language, using default: en');
      await this.changeLanguage('en', true);
      this.initialized = true;
      console.log('[Language Service] ✅ Initialization complete with default: en');
      return 'en';
    } catch (error) {
      console.error('[Language Service] ❌ Error initializing language:', error);
      // Fallback to English on error
      await this.changeLanguage('en', true);
      this.initialized = true;
      console.log('[Language Service] ⚠️ Initialization failed, fallback to: en');
      return 'en';
    }
  }

  /**
   * Resets initialization state (useful for testing or re-initialization)
   */
  resetInitialization(): void {
    this.initialized = false;
  }
}

// Export singleton instance
export const languageService = new LanguageService();
export default languageService;